import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Save, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DIGITOS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

const PERIODICIDADES = ['Mensual', 'Bimestral', 'Trimestral', 'Cuatrimestral', 'Anual'];

const PERIODOS_MAP = {
  Mensual: [
    'Enero 2026', 'Febrero 2026', 'Marzo 2026', 'Abril 2026',
    'Mayo 2026', 'Junio 2026', 'Julio 2026', 'Agosto 2026',
    'Septiembre 2026', 'Octubre 2026', 'Noviembre 2026', 'Diciembre 2026',
  ],
  Bimestral: [
    'Bimestre 1 (Ene-Feb 2026)', 'Bimestre 2 (Mar-Abr 2026)',
    'Bimestre 3 (May-Jun 2026)', 'Bimestre 4 (Jul-Ago 2026)',
    'Bimestre 5 (Sep-Oct 2026)', 'Bimestre 6 (Nov-Dic 2026)',
  ],
  Trimestral: [
    'Trimestre 1 (Ene-Mar 2026)', 'Trimestre 2 (Abr-Jun 2026)',
    'Trimestre 3 (Jul-Sep 2026)', 'Trimestre 4 (Oct-Dic 2026)',
  ],
  Cuatrimestral: [
    'Cuatrimestre 1 (Ene-Abr 2026)', 'Cuatrimestre 2 (May-Ago 2026)',
    'Cuatrimestre 3 (Sep-Dic 2026)',
  ],
  Anual: ['Año gravable 2025'],
};

const TaxCalendarManager = () => {
  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTax, setEditingTax] = useState(null);
  const [expandedTax, setExpandedTax] = useState(null);

  useEffect(() => {
    loadTaxes();
  }, []);

  const loadTaxes = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/settings/custom-taxes`);
      setTaxes(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (taxId) => {
    if (!window.confirm('¿Eliminar este impuesto personalizado?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/settings/custom-taxes/${taxId}`);
      setTaxes((prev) => prev.filter((t) => t._id !== taxId));
      if (expandedTax === taxId) setExpandedTax(null);
    } catch (error) {
      alert('Error al eliminar');
    }
  };

  const handleEdit = (tax) => {
    setEditingTax(tax);
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditingTax(null);
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingTax(null);
    loadTaxes();
  };

  const countDates = (tax) => {
    let total = 0;
    let filled = 0;
    if (tax.periodos) {
      for (const p of tax.periodos) {
        for (const d of DIGITOS) {
          total++;
          if (p.fechas && p.fechas[d]) filled++;
        }
      }
    }
    return { total, filled };
  };

  if (loading) return <div className="text-sm text-slate-500">Cargando impuestos...</div>;

  return (
    <div className="space-y-4" data-testid="tax-calendar-manager">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Calendario Tributario</h3>
          <p className="text-xs text-slate-500 mt-0.5">Cree impuestos personalizados con fechas de vencimiento por dígito del NIT</p>
        </div>
        <Button
          onClick={handleCreate}
          className="bg-[#1F3864] hover:bg-[#172b4d] text-white text-xs"
          data-testid="add-custom-tax-button"
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" />
          Nuevo Impuesto
        </Button>
      </div>

      {showForm && (
        <TaxForm
          tax={editingTax}
          onSuccess={handleFormSuccess}
          onCancel={() => { setShowForm(false); setEditingTax(null); }}
        />
      )}

      {taxes.length === 0 && !showForm ? (
        <div className="text-center py-8 text-slate-400 text-sm border border-dashed border-slate-200 rounded-lg">
          No hay impuestos personalizados. Haga clic en "Nuevo Impuesto" para crear uno.
        </div>
      ) : (
        <div className="space-y-2">
          {taxes.map((tax) => {
            const { total, filled } = countDates(tax);
            const isExpanded = expandedTax === tax._id;
            return (
              <div key={tax._id} className="border border-slate-200 rounded-lg overflow-hidden">
                <div
                  className="flex items-center justify-between px-4 py-3 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => setExpandedTax(isExpanded ? null : tax._id)}
                  data-testid={`tax-row-${tax._id}`}
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    <div>
                      <p className="font-medium text-sm text-slate-900">{tax.nombre}</p>
                      <p className="text-xs text-slate-500">
                        {tax.periodicidad} &middot; {tax.periodos?.length || 0} periodo(s) &middot; {filled}/{total} fechas
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => handleEdit(tax)} className="p-1.5 text-slate-400 hover:text-blue-600 rounded-md" title="Editar">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(tax._id)} className="p-1.5 text-slate-400 hover:text-red-600 rounded-md" title="Eliminar">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {isExpanded && tax.periodos && (
                  <div className="p-4 space-y-3 border-t border-slate-200">
                    {tax.periodos.map((periodo, idx) => (
                      <div key={idx}>
                        <p className="text-xs font-semibold text-slate-700 mb-2">{periodo.nombre}</p>
                        <div className="grid grid-cols-5 gap-2">
                          {DIGITOS.map((d) => (
                            <div key={d} className="text-xs">
                              <span className="text-slate-400">Dígito {d}:</span>{' '}
                              <span className={periodo.fechas?.[d] ? 'text-slate-900 font-medium' : 'text-slate-300'}>
                                {periodo.fechas?.[d] || '—'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const TaxForm = ({ tax, onSuccess, onCancel }) => {
  const [nombre, setNombre] = useState(tax?.nombre || '');
  const [periodicidad, setPeriodicidad] = useState(tax?.periodicidad || '');
  const [mesesSancion, setMesesSancion] = useState(tax?.meses_sancion || 1);
  const [periodos, setPeriodos] = useState(tax?.periodos || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (periodicidad && !tax) {
      const names = PERIODOS_MAP[periodicidad] || [];
      setPeriodos(names.map((name) => ({ nombre: name, fechas: {} })));
    }
  }, [periodicidad]);

  const handleDateChange = (periodoIdx, digito, value) => {
    setPeriodos((prev) => {
      const updated = [...prev];
      updated[periodoIdx] = {
        ...updated[periodoIdx],
        fechas: { ...updated[periodoIdx].fechas, [digito]: value },
      };
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    const payload = { nombre, periodicidad, periodos, meses_sancion: mesesSancion };

    try {
      if (tax) {
        await axios.put(`${BACKEND_URL}/api/settings/custom-taxes/${tax._id}`, payload);
      } else {
        await axios.post(`${BACKEND_URL}/api/settings/custom-taxes`, payload);
      }
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="border border-blue-200 bg-blue-50/30 rounded-lg p-5 space-y-4" data-testid="custom-tax-form">
      <div className="flex items-center justify-between">
        <h4 className="font-bold text-sm text-slate-900">{tax ? 'Editar Impuesto' : 'Nuevo Impuesto Personalizado'}</h4>
        <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-600">
          <X className="w-4 h-4" />
        </button>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2">{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label className="text-xs font-semibold uppercase text-slate-500">Nombre del Impuesto *</Label>
          <Input
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            required
            placeholder="Ej: Impuesto Predial"
            className="mt-1"
            data-testid="custom-tax-name"
          />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase text-slate-500">Periodicidad *</Label>
          <select
            value={periodicidad}
            onChange={(e) => setPeriodicidad(e.target.value)}
            className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm mt-1"
            required
            data-testid="custom-tax-periodicidad"
          >
            <option value="">Seleccionar...</option>
            {PERIODICIDADES.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase text-slate-500">Meses sanción</Label>
          <Input
            type="number"
            min="0"
            value={mesesSancion}
            onChange={(e) => setMesesSancion(parseInt(e.target.value) || 0)}
            className="mt-1"
          />
        </div>
      </div>

      {periodos.length > 0 && (
        <div className="space-y-4">
          <p className="text-xs font-semibold uppercase text-slate-500">
            Fechas de vencimiento por último dígito del NIT
          </p>
          {periodos.map((periodo, periodoIdx) => (
            <div key={periodoIdx} className="bg-white border border-slate-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-slate-800 mb-3">{periodo.nombre}</p>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {DIGITOS.map((d) => (
                  <div key={d}>
                    <label className="text-xs text-slate-500 block mb-1">Dígito {d}</label>
                    <Input
                      type="date"
                      value={periodo.fechas?.[d] || ''}
                      onChange={(e) => handleDateChange(periodoIdx, d, e.target.value)}
                      className="text-xs h-8"
                      data-testid={`date-${periodoIdx}-${d}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} className="text-xs">
          Cancelar
        </Button>
        <Button type="submit" disabled={saving || !periodicidad} className="bg-[#1F3864] hover:bg-[#172b4d] text-white text-xs" data-testid="save-custom-tax">
          <Save className="w-3.5 h-3.5 mr-1.5" />
          {saving ? 'Guardando...' : tax ? 'Actualizar' : 'Crear Impuesto'}
        </Button>
      </div>
    </form>
  );
};

export default TaxCalendarManager;
